import { el } from '@/ui/dom';
import { SP_COIN_SVG, XP_STAR_SVG } from '@/ui/currencyIcons';

/**
 * SkillPointsToast.ts — the "+N XP · +N SP" pop notification (M78, renamed
 * M131), for award moments that have no results card on screen (online tour
 * intermissions, the guest-pot claim). A gold glass chip drops in
 * top-centre, star-burst pulses, holds, and floats away. One at a time — a
 * new toast replaces the last. Also exports the shared count-up ticker the
 * results card + leaderboard use.
 *
 * M131: racing pays XP and SP 1:1 from ONE award, so the default toast shows
 * both halves side by side (gold star XP, green coin SP). A bought pack pays
 * SP only — `kind: 'sp'` shows just the green half.
 */

export type ToastKind = 'both' | 'sp';

export class SkillPointsToast {
  private readonly layer: HTMLDivElement;
  private hideTimer: number | null = null;

  constructor(parent: HTMLElement) {
    this.layer = el('div', 'sp-toast-layer', parent);
  }

  /** Pop "+amount XP · +amount SP" (optionally with an unlock line under it). */
  show(amount: number, unlockLabel?: string, kind: ToastKind = 'both'): void {
    if (amount <= 0) return;
    if (this.hideTimer !== null) window.clearTimeout(this.hideTimer);
    this.layer.replaceChildren();

    const toast = el('div', `sp-toast${kind === 'sp' ? ' sp-toast-sponly' : ''}`, this.layer);
    const body = el('span', 'sp-toast-body', toast);
    if (kind === 'both') {
      const xp = el('span', 'sp-toast-half sp-toast-xp', body);
      const star = el('span', 'sp-toast-star', xp);
      star.innerHTML = XP_STAR_SVG;
      el('span', 'sp-toast-amount', xp).textContent = `+${amount}`;
      el('span', 'sp-toast-label', xp).textContent = 'XP';
      el('span', 'sp-toast-dot', body).textContent = '·';
    }
    const sp = el('span', 'sp-toast-half sp-toast-sp', body);
    const coin = el('span', 'sp-toast-star sp-toast-coin', sp);
    coin.innerHTML = SP_COIN_SVG;
    el('span', 'sp-toast-amount', sp).textContent = `+${amount}`;
    el('span', 'sp-toast-label', sp).textContent = 'SP';
    if (unlockLabel) {
      const sub = el('span', 'sp-toast-unlock', toast);
      sub.textContent = `UNLOCKED: ${unlockLabel.toUpperCase()}`;
    }
    this.hideTimer = window.setTimeout(() => {
      toast.classList.add('out');
      this.hideTimer = window.setTimeout(() => {
        this.layer.replaceChildren();
        this.hideTimer = null;
      }, 500);
    }, 2600);
  }

  dispose(): void {
    if (this.hideTimer !== null) window.clearTimeout(this.hideTimer);
    this.layer.remove();
  }
}

/**
 * Roll a number element from → to over `ms` (ease-out, integers). Reduced
 * motion (the game setting, passed by the caller) lands instantly. Returns a
 * cancel function — call it if the element is about to be torn down.
 */
export function animateCount(
  target: HTMLElement,
  from: number,
  to: number,
  ms: number,
  reduced: boolean,
  format: (n: number) => string = (n) => String(n),
): () => void {
  if (reduced || ms <= 0 || from === to) {
    target.textContent = format(to);
    return () => {};
  }
  target.textContent = format(from);
  let raf = 0;
  const start = performance.now();
  const tick = (now: number): void => {
    const t = Math.min(1, (now - start) / ms);
    const eased = 1 - Math.pow(1 - t, 3);
    target.textContent = format(Math.round(from + (to - from) * eased));
    if (t < 1) raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}
